import React from 'react';
import Box, { BoxProps } from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import { InfoIcon } from '@common/utils/icons/icons';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';

const DashboardSection: React.FC<{
  title: string;
  info?: React.ReactNode;
  caption?: string;
  actions?: React.ReactNode;
  sx?: BoxProps['sx'];
  children: React.ReactNode;
}> = ({ title, info, caption, actions, sx, children }) => {
  const matchesMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [infoOpen, setInfoOpen] = React.useState(false);

  return (
    <Box sx={[{ p: { xs: 2, md: 3 }, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Stack gap={{ xs: 2, md: 3 }} height="100%">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
        >
          <Stack gap={0.25} minWidth={0}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                {title}
              </Typography>
              {info && matchesMdUp && (
                <Tooltip
                  title={info}
                  componentsProps={{
                    tooltip: { sx: { maxWidth: 500, p: 1.5, fontSize: '0.8rem' } },
                  }}
                >
                  <Box
                    component="span"
                    tabIndex={0}
                    aria-label={typeof info === 'string' ? info : `${title} info`}
                    display="flex"
                    sx={{ color: 'text.disabled', cursor: 'help' }}
                  >
                    <InfoIcon fontSize="small" />
                  </Box>
                </Tooltip>
              )}
              {info && !matchesMdUp && (
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  aria-label={typeof info === 'string' ? info : `${title} info`}
                  display="flex"
                  onClick={() => setInfoOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setInfoOpen(true);
                  }}
                  sx={{ color: 'text.disabled', cursor: 'pointer' }}
                >
                  <InfoIcon fontSize="small" />
                </Box>
              )}
            </Stack>
            {caption && (
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            )}
          </Stack>
          {actions}
        </Stack>
        {children}
      </Stack>
      {info && !matchesMdUp && (
        <DialogWrapperWithClose open={infoOpen} onClose={() => setInfoOpen(false)} title={title}>
          <DialogContent>{info}</DialogContent>
        </DialogWrapperWithClose>
      )}
    </Box>
  );
};

export default DashboardSection;
